/**
 * Jungle mobs (panda, parrot) and the fox. Real vanilla geometry (ModelPart boxes, pivots,
 * UV offsets) over the real entity sheets in public/mc/entity (model px space,
 * y down, front -z, feet at y = 24).
 */

import type { EnvUniforms } from '../env';
import { sheetTexture } from './core';
import type { OwAnim } from './overworldModels';
import { Rig } from './overworldModels2';

const PI = Math.PI;

// ------------------------------------------------------------------- panda

/** Sheet per visible gene (entities/overworld2/jungle.ts PANDA_GENES order). */
const PANDA_SKINS = ['panda', 'panda_lazy', 'panda_worried', 'panda_playful', 'panda_brown', 'panda_weak', 'panda_aggressive'] as const;

/**
 * PandaModel (64x64). `view.color`: bits 0-2 gene, bit 3 sitting, bit 4 eating.
 * Babies are the pre-26.1 scaled adult (AgeableListModel: body 1/3, head 1.5/2.7).
 */
export function pandaAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/panda/panda', 64, 64);
  let skin = 0;
  // PandaRenderer.setupRotations: a sitting panda is tipped back 90 degrees about y = 16 px
  const tip = r.pivot([0, 0, 0]);
  const inner = r.pivot([0, 0, 0], tip);
  const headRoot = r.pivot([0, 0, 0], inner), bodyRoot = r.pivot([0, 0, 0], inner);
  const head = r.pivot([0, 11.5, -17], headRoot);
  r.box(head, [0, 6], [-6.5, -5, -4], [13, 10, 9]);
  r.box(head, [45, 16], [-3.5, 0, -6], [7, 5, 2]);
  r.box(head, [52, 25], [3.5, -8, -1], [5, 4, 1]);
  r.box(head, [52, 25], [-8.5, -8, -1], [5, 4, 1]);
  const body = r.pivot([0, 10, 0], bodyRoot, [PI / 2, 0, 0]);
  r.box(body, [0, 25], [-9.5, -13, -6.5], [19, 26, 13]);
  const hr = r.pivot([-5.5, 15, 9], bodyRoot), hl = r.pivot([5.5, 15, 9], bodyRoot);
  const fr = r.pivot([-5.5, 15, -9], bodyRoot), fl = r.pivot([5.5, 15, -9], bodyRoot);
  for (const leg of [hr, hl, fr, fl]) r.box(leg, [40, 0], [-3, 0, -3], [6, 9, 6]);
  return r.anim((v, q, headY, headX, age) => {
    const gene = v.color & 7;
    if (gene !== skin) { skin = gene; r.mat.uniforms.uMap.value = sheetTexture(`entity/panda/${PANDA_SKINS[gene] ?? 'panda'}`); }
    const sit = (v.color & 8) !== 0, eating = (v.color & 16) !== 0;
    if (v.baby) {
      const hs = 1.5 / 2.7, bs = 1 / 3;
      headRoot.scale.setScalar(hs); headRoot.position.set(0, 23 * hs, 4.8 * hs);
      bodyRoot.scale.setScalar(bs); bodyRoot.position.set(0, 49 * bs, 0);
    } else {
      headRoot.scale.setScalar(1); headRoot.position.set(0, 0, 0);
      bodyRoot.scale.setScalar(1); bodyRoot.position.set(0, 0, 0);
    }
    const s = q.limbSwing * 0.6662, a = q.limbAmt;
    hr.rotation.set(Math.cos(s) * 1.4 * a, 0, 0); hl.rotation.set(Math.cos(s + PI) * 1.4 * a, 0, 0);
    fr.rotation.set(Math.cos(s + PI) * 1.4 * a, 0, 0); fl.rotation.set(Math.cos(s) * 1.4 * a, 0, 0);
    head.rotation.set(headX, headY, 0);
    if (sit) {
      tip.position.y = 11.2; tip.rotation.x = -PI / 2; inner.position.y = -8;
      body.rotation.x = 1.7407963;
      head.rotation.set(PI / 2, headY, 0);
      fr.rotation.z = -0.27079642; fl.rotation.z = 0.27079642;
      hr.rotation.z = 0.5707964; hl.rotation.z = -0.5707964;
      if (eating) {
        const chew = Math.sin(age * 0.6);
        head.rotation.x = PI / 2 + 0.2 * chew;
        fr.rotation.x = fl.rotation.x = -0.4 - 0.2 * chew;
      }
    } else {
      tip.position.y = 0; tip.rotation.x = 0; inner.position.y = 0;
      body.rotation.x = PI / 2;
    }
  });
}

// ------------------------------------------------------------------ parrot

/** Sheet per variant (vanilla Parrot.Variant order). */
const PARROT_SKINS = ['parrot_red_blue', 'parrot_blue', 'parrot_green', 'parrot_yellow_blue', 'parrot_grey'] as const;

/** ParrotModel (32x32). `view.color`: bits 0-2 variant, bit 3 tame, bit 4 sitting, bit 5 flying. */
export function parrotAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/parrot/parrot_red_blue', 32, 32, { doubleSided: true });
  let skin = 0;
  const body = r.pivot([0, 16.5, -3], r.parts, [0.4937, 0, 0]);
  r.box(body, [2, 8], [-1.5, 0, -1.5], [3, 6, 3]);
  const tail = r.pivot([0, 21.07, 1.16], r.parts, [1.015, 0, 0]);
  r.box(tail, [22, 1], [-1.5, -1, -1], [3, 4, 1]);
  const wingL = r.pivot([1.5, 16.94, -2.76], r.parts, [-0.6981, -PI, 0]);
  r.box(wingL, [19, 8], [-0.5, 0, -1.5], [1, 5, 3]);
  const wingR = r.pivot([-1.5, 16.94, -2.76], r.parts, [-0.6981, -PI, 0]);
  r.box(wingR, [19, 8], [-0.5, 0, -1.5], [1, 5, 3]);
  const head = r.pivot([0, 15.69, -2.76]);
  r.box(head, [2, 2], [-1, -1.5, -1], [2, 3, 2]);
  r.box(r.pivot([0, -2, -1], head), [10, 0], [-1, -0.5, -2], [2, 1, 4]);
  r.box(r.pivot([0, -0.5, -1.5], head), [11, 7], [-0.5, -1, -0.5], [1, 2, 1]);
  r.box(r.pivot([0, -1.75, -2.45], head), [16, 7], [-0.5, 0, -0.5], [1, 2, 1]);
  r.box(r.pivot([0, -2.15, 0.15], head, [-0.2214, 0, 0]), [2, 18], [0, -4, -2], [0, 5, 4]);
  const legL = r.pivot([1, 22, -1.05], r.parts, [-0.0299, 0, 0]), legR = r.pivot([-1, 22, -1.05], r.parts, [-0.0299, 0, 0]);
  r.box(legL, [14, 18], [-0.5, 0, -0.5], [1, 2, 1]);
  r.box(legR, [14, 18], [-0.5, 0, -0.5], [1, 2, 1]);
  return r.anim((v, q, headY, headX, age) => {
    const variant = v.color & 7;
    if (variant !== skin) { skin = variant; r.mat.uniforms.uMap.value = sheetTexture(`entity/parrot/${PARROT_SKINS[variant] ?? PARROT_SKINS[0]}`); }
    const sitting = (v.color & 16) !== 0, flying = (v.color & 32) !== 0 && !sitting;
    head.rotation.set(headX, headY, 0);
    if (sitting) {
      // ParrotModel.prepare SITTING: everything drops 1.9 px, legs fold forward
      head.position.y = 17.59; body.position.y = 18.4;
      tail.position.y = 22.97; tail.rotation.x = 1.5388988;
      wingL.position.y = wingR.position.y = 18.84;
      wingL.rotation.z = -0.0873; wingR.rotation.z = 0.0873;
      legL.position.y = legR.position.y = 23.9;
      legL.rotation.x = legR.rotation.x = -0.0299 + PI / 2;
      return;
    }
    // vanilla: flap = (sin(flap) + 1) * flapSpeed, flap advancing 2 rad / tick in the air
    const flap = flying ? Math.sin(age * 1.2 + v.id) + 1 : 0;
    const bob = flap * 0.3;
    const s = q.limbSwing * 0.6662, a = q.limbAmt;
    head.position.y = 15.69 + bob; body.position.y = 16.5 + bob;
    tail.position.y = 21.07 + bob; tail.rotation.x = 1.015 + Math.cos(s) * 0.3 * a;
    wingL.position.y = wingR.position.y = 16.94 + bob;
    wingL.rotation.z = -0.0873 - flap; wingR.rotation.z = 0.0873 + flap;
    legL.position.y = legR.position.y = 22 + bob;
    if (flying) legL.rotation.x = legR.rotation.x = -0.0299 + (PI * 2) / 9;
    else {
      legL.rotation.x = -0.0299 + Math.cos(s) * 1.4 * a;
      legR.rotation.x = -0.0299 + Math.cos(s + PI) * 1.4 * a;
    }
  });
}

// --------------------------------------------------------------------- fox

/**
 * FoxModel (48x32). `view.color`: bit 0 snow fox, bit 1 sitting, bit 2 sleeping (curled up,
 * eyes-closed sheet). Kits are the pre-26.1 scaled adult (body 1/2, head 3/4).
 */
export function foxAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/fox/fox', 48, 32);
  let skin = 'fox';
  const headRoot = r.pivot([0, 0, 0]), bodyRoot = r.pivot([0, 0, 0]);
  const head = r.pivot([-1, 16.5, -3], headRoot);
  r.box(head, [1, 5], [-3, -2, -5], [8, 6, 6]);
  r.box(head, [8, 1], [-3, -4, -4], [2, 2, 1]);
  r.box(head, [15, 1], [3, -4, -4], [2, 2, 1]);
  r.box(head, [6, 18], [-1, 2.01, -8], [4, 2, 3]);
  const body = r.pivot([0, 16, -6], bodyRoot, [PI / 2, 0, 0]);
  r.box(body, [24, 15], [-3, 3.999, -3.5], [6, 11, 6]);
  const tail = r.pivot([-4, 15, -1], body, [-0.05235988, 0, 0]);
  r.box(tail, [30, 0], [2, 0, -1], [4, 9, 5]);
  const hr = r.pivot([-5, 17.5, 7], bodyRoot), hl = r.pivot([-1, 17.5, 7], bodyRoot);
  const fr = r.pivot([-5, 17.5, 0], bodyRoot), fl = r.pivot([-1, 17.5, 0], bodyRoot);
  r.box(hr, [13, 24], [2, 0.5, -1], [2, 6, 2], 0.001); r.box(hl, [4, 24], [2, 0.5, -1], [2, 6, 2], 0.001);
  r.box(fr, [13, 24], [2, 0.5, -1], [2, 6, 2], 0.001); r.box(fl, [4, 24], [2, 0.5, -1], [2, 6, 2], 0.001);
  const legs = [hr, hl, fr, fl];
  return r.anim((v, q, headY, headX) => {
    const sitting = (v.color & 2) !== 0, sleeping = (v.color & 4) !== 0;
    const want = `fox${v.color & 1 ? '_snow' : ''}${sleeping ? '_sleep' : ''}`;
    if (want !== skin) { skin = want; r.mat.uniforms.uMap.value = sheetTexture(`entity/fox/${want}`); }
    if (v.baby) {
      headRoot.scale.setScalar(0.75); headRoot.position.set(0, 6, 2.5125);
      bodyRoot.scale.setScalar(0.5); bodyRoot.position.set(0, 12, 0);
    } else {
      headRoot.scale.setScalar(1); headRoot.position.set(0, 0, 0);
      bodyRoot.scale.setScalar(1); bodyRoot.position.set(0, 0, 0);
    }
    // rest pose, then FoxModel.prepareMobModel's sleeping / sitting overrides
    const s = q.limbSwing * 0.6662, a = q.limbAmt;
    body.position.set(0, 16, -6); body.rotation.set(PI / 2, 0, 0);
    tail.position.set(-4, 15, -1); tail.rotation.x = -0.05235988;
    head.position.set(-1, 16.5, -3); head.rotation.set(headX, headY, 0);
    hr.position.set(-5, 17.5, 7); hl.position.set(-1, 17.5, 7);
    hr.rotation.x = Math.cos(s) * 1.4 * a; hl.rotation.x = Math.cos(s + PI) * 1.4 * a;
    fr.rotation.x = Math.cos(s + PI) * 1.4 * a; fl.rotation.x = Math.cos(s) * 1.4 * a;
    for (const l of legs) l.visible = !sleeping;
    if (sleeping) {
      body.rotation.z = -PI / 2; body.position.set(0, 21, -6);
      tail.rotation.x = -2.6179938;
      head.position.set(1, 19.49, -3); head.rotation.set(0, -2.0943952, 0);
    } else if (sitting) {
      body.rotation.x = PI / 6; body.position.set(0, 9, -3);
      tail.rotation.x = PI / 4; tail.position.set(-4, 15, -2);
      head.position.set(-1, 10, -0.25); head.rotation.set(0, 0, 0);
      hr.rotation.x = hl.rotation.x = -1.3089969;
      hr.position.set(-5, 21.5, 6.75); hl.position.set(-1, 21.5, 6.75);
      fr.rotation.x = fl.rotation.x = -0.2617994;
    }
  });
}
